import { cloneProperties } from './utilities/icon/clone';
import {
  Transforms,
  Editor,
  Element as SlateElement,
  Range,
  Node,
  Path,
  Text
} from 'slate';
import { ReactEditor } from 'slate-react';

import { toggleBlock } from 'generics/components/basic-text-editor-slate/3rd-party/rich-text';

import types, { blockLevelElements, textElements, indentationChildren } from '../elements/types';

import * as baseNodeUtilities from './utilities/base-node';
import * as elementUtilities from './utilities/element';
import * as pathUtilities from './utilities/path';
import * as paragraphUtilities from './utilities/paragraph';
import * as textUtilities from './utilities/text';
import * as formUtilities from './utilities/form';
import * as buttonUtilities from './utilities/button';
import * as authenticatorButtonUtilities from './utilities/authenticator-button';
import * as headingUtilities from './utilities/heading';
import * as fileUtilities from './utilities/file';
import * as listUtilities from './utilities/list';
import * as linkUtilities from './utilities/link';
import * as htmlImporterUtilities from './utilities/html-importer';
import * as urlEmbedUtilities from './utilities/url-embed';
import * as sourceCodeRegionUtilities from './utilities/source-code-region';
import * as duplicateUtilities from './utilities/duplicate';
import * as blockQuoteUtilities from './utilities/block-quote';
import * as dividerUtilities from './utilities/divider';
import * as multipleColumnsUtilities from './utilities/multiple-columns';
import * as layoutUtilities from './utilities/layout';
import { handleEnter as handleHeaderEnter } from './utilities/site-nav/typing';
import * as tableOfContentsUtilities from './utilities/table-of-contents';
import * as tableUtilities from './utilities/table';
import * as productUtilities from './utilities/product';
import * as synchronizedPageRegionUtilities from './utilities/synchronized-page-region';
import * as nodeConversationUtilities from './utilities/node-conversion';
import * as memberCalendarUtilities from './utilities/member-calendar';
import * as dataCollectionUtilities from './utilities/data-collection';
import * as dragAndDropUtilities from './utilities/drag-and-drop';
import * as toggleListItemUtilities from './utilities/toggle-list-item';
import * as singleProducerMeetingUtilities from './utilities/single-producer-meeting';
import * as indentationUtilities from './utilities/indentation';
import * as templateClonerUtilities from './utilities/template-cloner';
import * as copyPasteUtilities from './utilities/copy-paste';
import * as canvasUtilities from './utilities/canvas';
import * as videoCanvasUtilities from './utilities/video-canvas';
import * as signaturePanelUtilities from './utilities/signature-panel';
import * as lastUpdatedUtilities from './utilities/last-updated';
import * as pageAuthorUtilities from './utilities/page-author';
import * as aiAssistantUtilities from './utilities/ai-assistant';
import * as youtubeUtilities from './utilities/youtube';
import * as directoryUtilities from './utilities/directory';
import * as galleryUtilities from './utilities/gallery';
import * as carouselUtilities from './utilities/carousel';
import * as serverUpdateUtilities from './utilities/server-update';
import * as mentionUtilities from './utilities/mention';

import * as serializer from 'basic-editor-value-serializer';

const marks = [
  'bold',
  'italic',
  'underline',
  'strikethrough',
  'textColor',
  'backgroundColor',
  'fontSize',
  'fontFamily',
  'lineHeight',
  'alignment'
];

const WHITE_SPACE_CHARACTER = ' ';

const textContainers = [
  types.DEFAULT,
  types.HEADING_ONE,
  types.HEADING_TWO,
  types.HEADING_THREE,
  types.HEADING_FOUR,
  types.HEADING_FIVE,
  types.HEADING_SIX,
  types.BLOCK_QUOTE,
  types.LIST_ITEM,
  types.NUMBERED_LIST,
  types.NUMBERED_LIST_ITEM,
  types.CHECK_LIST_ITEM,
  types.TABLE_BODY,
  types.TABLE_ROW,
  types.TABLE_CELL,
  types.BULLETED_LIST,
  types.BULLETED_LIST_ITEM,
  types.TOGGLE_LIST_HEAD,
  types.TOGGLE_LIST_ITEM,
  types.TOGGLE_LIST_BODY,
  types.COLUMNS,
  types.COLUMN
];

// This is moved to ./utilities/default-properties.js
export const defaultStyles = {
  padding: [5, 5, 5, 5],
  margin: [0, 0, 0, 0]
};

function isMarkActive(editor, format) {

  const marks = Editor.marks(editor);

  return marks ? marks[format] === true : false;

}

function isTextAttributeExists(editor, format) {

  const marks = Editor.marks(editor);

  if (marks === null) {
    return false;
  }

  return typeof marks[format] !== 'undefined';

}

function isLinkNode(node) {

  return !Editor.isEditor(node) && SlateElement.isElement(node) && node.type === 'link';

}

function isMentionNode(node) {

  return !Editor.isEditor(node) && SlateElement.isElement(node) && node.type === 'mention';

}

function hasAttribute(attribute) {

  return function doHasAttribute(node) {

    if (Editor.isEditor(node) === true) {
      return false;
    }

    if (SlateElement.isElement(node) === false) {
      return false;
    }

    return typeof node[attribute] !== 'undefined';

  };

}

const HelptapEditor = {
  ...Editor,
  mention: mentionUtilities,
  path: pathUtilities,
  text: textUtilities,
  baseNode: baseNodeUtilities,
  element: elementUtilities,
  form: formUtilities,
  button: buttonUtilities,
  authenticatorButton: authenticatorButtonUtilities,
  paragraph: paragraphUtilities,
  heading: headingUtilities,
  file: fileUtilities,
  list: listUtilities,
  link: linkUtilities,
  htmlImporter: htmlImporterUtilities,
  urlEmbed: urlEmbedUtilities,
  sourceCodeRegion: sourceCodeRegionUtilities,
  duplicate: duplicateUtilities,
  blockQuote: blockQuoteUtilities,
  divider: dividerUtilities,
  multipleColumns: multipleColumnsUtilities,
  layout: layoutUtilities,
  tableOfContents: tableOfContentsUtilities,
  table: tableUtilities,
  product: productUtilities,
  synchronizedPageRegion: synchronizedPageRegionUtilities,
  memberCalendar: memberCalendarUtilities,
  dataCollection: dataCollectionUtilities,
  dragAndDrop: dragAndDropUtilities,
  toggleListItem: toggleListItemUtilities,
  singleProducerMeeting: singleProducerMeetingUtilities,
  indentation: indentationUtilities,
  templateCloner: templateClonerUtilities,
  conversion: nodeConversationUtilities,
  copyPaste: copyPasteUtilities,
  canvas: canvasUtilities,
  videoCanvas: videoCanvasUtilities,
  signaturePanel: signaturePanelUtilities,
  lastUpdated: lastUpdatedUtilities,
  pageAuthor: pageAuthorUtilities,
  aiAssistant: aiAssistantUtilities,
  youtube: youtubeUtilities,
  directory: directoryUtilities,
  gallery: galleryUtilities,
  carousel: carouselUtilities,
  serverUpdate: serverUpdateUtilities,
  isBoldMarkActive(editor) {
    return isMarkActive(editor, 'bold');
  },
  isItalicMarkActive(editor) {
    return isMarkActive(editor, 'italic');
  },
  isCodeBlockActive(editor) {
    return isMarkActive(editor, 'code');
  },
  isUnderlineMarkActive(editor) {
    return isMarkActive(editor, 'underline');
  },
  isStrikeThroughMarkActive(editor) {
    return isMarkActive(editor, 'strikethrough');
  },
  toggleBoldMark(editor) {

    const isActive = HelptapEditor.isBoldMarkActive(editor);

    if (isActive === true) {
      HelptapEditor.removeMark(editor, 'bold');
    } else {
      HelptapEditor.addMark(editor, 'bold', true);
    }

  },
  toggleItalicMark(editor) {

    const isActive = HelptapEditor.isItalicMarkActive(editor);

    if (isActive) {
      HelptapEditor.removeMark(editor, 'italic');
    } else {
      HelptapEditor.addMark(editor, 'italic', true);
    }

  },
  toggleCodeBlock(editor) {

    const isActive = HelptapEditor.isCodeBlockActive(editor);

    if (isActive === true) {
      HelptapEditor.removeMark(editor, 'code');
    } else {
      HelptapEditor.addMark(editor, 'code', true);
    }

  },
  toggleUnderlineMark(editor) {

    const isActive = HelptapEditor.isUnderlineMarkActive(editor);

    if (isActive === true) {
      HelptapEditor.removeMark(editor, 'underline');
    } else {
      HelptapEditor.addMark(editor, 'underline', true);
    }

  },
  toggleStrikeThroughMark(editor) {

    const isActive = HelptapEditor.isStrikeThroughMarkActive(editor);

    if (isActive === true) {
      HelptapEditor.removeMark(editor, 'strikethrough');
    } else {
      HelptapEditor.addMark(editor, 'strikethrough', true);
    }

  },
  toggleMarkWithAppend(editor, format) {

    if (isMarkActive(editor, format) === true) {

    }

    // Transforms.setNodes(
    //   editor, { bold: isActive ? null : true }, {
    //     match(node) {
    //       return Text.isText(node);
    //     },
    //     split: true
    //   }
    // );

  },
  isBlockActive(editor, format) {

    const { selection } = editor;

    if (!selection) {
      return false;
    }

    function isNodeByFormat(node) {

      if (Editor.isEditor(node) === true) {
        return false;
      }

      if (SlateElement.isElement(node) === false) {
        return false;
      }

      return node.type === format;

    }

    const nodes = Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: isNodeByFormat
    });

    const [match] = Array.from(nodes);

    return !!match;
  },
  isAlignmentMarkActive(editor, alignmentType) {

    const { selection } = editor;
    if (!selection) return false;

    const entry = Editor.above(editor, {
      at: Editor.unhangRange(editor, selection),
      match: n => SlateElement.isElement(n) && Editor.isBlock(editor, n),
      voids: true,
    });

    if (!entry) return false;
    const [block] = entry;
    return block.alignment === alignmentType;
  },
  toggleAlignmentMark(editor, alignmentType) {

    if (HelptapEditor.isAlignmentMarkActive(editor, alignmentType) === true) {
      return Transforms.setNodes(editor, { alignment: 'left' });
    }

    Transforms.setNodes(editor, { alignment: alignmentType });

  },
  createDefaultBlock() {

    const block = {
      ...defaultStyles,
      type: types.DEFAULT,
      children: [{ text: '' }]
      // resizable: true
    };

    return block;

  },
  setNodeOptions(editor, nodeOptions, editorOptions) {
    Transforms.setNodes(editor, nodeOptions, editorOptions);
  },
  wrapNode(editor, nodeOptions) {
    HelptapEditor.setNodeOptions(editor, nodeOptions);
    Transforms.wrapNodes(editor, nodeOptions);
  },
  insertNode(editor, node, path) {

    Transforms.insertNodes(editor, node, { at: path });

    ReactEditor.focus(editor);

  },
  insertBlock(editor, type, blockOptions = {}, path) {

    const node = [{
      ...baseNodeUtilities.getDefaultOptions(),
      ...blockOptions,
      type
    }];

    HelptapEditor.insertNode(editor, node, path);

  },
  insertListBlock(editor, type, blockOptions = {}) {

    listUtilities.insertBlock(editor, type, blockOptions);

  },
  insertCheckListBlock(editor, blockOptions) {

    const listItem = {
      text: ''
    };

    let listNode = {
      type: types.CHECK_LIST_ITEM,
      ...baseNodeUtilities.getDefaultOptions(),
      ...blockOptions,
      children: [listItem]
    };

    const previousNode = HelptapEditor.getPreviousNode(editor);

    if (previousNode.type === 'list-item') {

      const [parentNode, parentPath] = Editor.parent(editor, editor.selection.anchor);

      blockOptions.at = parentPath.slice(0, -1);
      blockOptions.at[blockOptions.at.length - 1]++;

    }

    Transforms.insertNodes(editor, listNode, blockOptions);
    HelptapEditor.moveCursorToStartOfTheLine(editor, blockOptions.at);
    ReactEditor.focus(editor, blockOptions);

  },
  insertParagraph(editor, insertOptions, path) {

    return HelptapEditor.insertBlock(editor, types.DEFAULT, insertOptions, path);

  },
  insertHeading(editor, type, insertOptions) {

    return HelptapEditor.insertBlock(editor, type, insertOptions);

  },
  isLinkActive(editor) {

    const [link] = Editor.nodes(editor, {
      match: isLinkNode
    });

    return !!link;

  },
  unwrapLink(editor) {

    Transforms.unwrapNodes(editor, {
      match: isLinkNode
    });

    // Move the cursor to the end of the selection
    Transforms.collapse(editor, { edge: 'end' });

    ReactEditor.focus(editor);

  },
  wrapLinkAt(editor, url, text, location) {

    const link = {
      type: 'link',
      url,
      children: [{
        text
      }]
    };

    Transforms.wrapNodes(editor, link, { split: true, at: location });
    Transforms.select(editor, {
      anchor: {
        path: Path.next(Path.next(location.focus.path)),
        offset: 0
      },
      focus: {
        path: Path.next(Path.next(location.focus.path)),
        offset: 0
      }
    });
  },
  wrapLink(editor, url, text) {

    if (HelptapEditor.isLinkActive(editor) === true) {
      Transforms.setNodes(editor, { url }, {
        match: isLinkNode
      });
      return false;
    }

    const { selection } = editor;

    const isCollapsed = selection && Range.isCollapsed(selection);
    const link = {
      type: 'link',
      url,
      children: []
    };

    if (isCollapsed === true) {

      let _text = text;

      if (typeof _text === 'undefined') {
        _text = url;
      }

      link.children = [{ text: _text, bold: true }];
    }

    if (isCollapsed === true) {
      Transforms.insertNodes(editor, link);
    } else {
      Transforms.wrapNodes(editor, link, { split: true });
      HelptapEditor.addMark(editor, 'bold', true);
      // Move the cursor to the end
      Transforms.collapse(editor, { edge: 'end' });
    }

    ReactEditor.focus(editor);

    return true;

  },
  updateLink: (editor, link) => {
    const { selection } = editor;
    if (!selection) return;

    // Normalize the link parameter
    const normalizedLink = link ? link.trim() : '';

    // Check if current selection is within a link
    const linkEntries = Array.from(
      Editor.nodes(editor, {
        match: isLinkNode,
        mode: 'lowest',
      })
    );
    const isCurrentlyLink = linkEntries.length > 0;
    // Case 1: Link is empty and current selection is a link - unwrap it
    if (!normalizedLink && isCurrentlyLink) {
      Transforms.setNodes(
        editor, { bold: false }, {
          at: linkEntries[0][1],
          match: n => Text.isText(n) && n.bold === true,
        }
      );
      Transforms.unwrapNodes(editor, {
        match: isLinkNode,
      });

      ReactEditor.focus(editor);

      return;
    }

    // Case 2: Link is present and current selection is not a link - wrap as link
    if (normalizedLink && !isCurrentlyLink) {
      // Handle collapsed selection
      if (Range.isCollapsed(selection)) {
        // Insert link with the URL as text and make it bold
        const linkNode = {
          type: 'link',
          url: normalizedLink,
          children: [{ text: normalizedLink, bold: true }],
        };
        Transforms.insertNodes(editor, linkNode);
        // Move cursor after the inserted link
        Transforms.move(editor);
        return;
      }

      // For non-collapsed selection, wrap in link and make text bold
      const selectedText = Editor.string(editor, selection);
      if (selectedText) {
        // First apply bold formatting to the selected text
        Transforms.setNodes(
          editor, { bold: true }, {
            match: Text.isText,
            split: true,
          }
        );

        // Then wrap in link
        const linkNode = {
          type: 'link',
          url: normalizedLink,
          children: [],
        };

        Transforms.wrapNodes(editor, linkNode, {
          split: true,
          match: Text.isText,
        });
      }
      return;
    }

    // Case 3: Link is present and current selection is a link - update the URL
    if (normalizedLink && isCurrentlyLink) {
      // Update all link nodes in the selection
      Transforms.setNodes(
        editor, { url: normalizedLink }, {
          match: isLinkNode,
          mode: 'lowest',
        }
      );
      return;
    }
  },
  insertLink(editor, url, text, options) {

    const wrapped = HelptapEditor.wrapLink(editor, url, text, options);
    Transforms.select(editor, {
      anchor: {
        path: Path.next(Path.parent(editor.selection.focus.path)),
        offset: 0
      },
      focus: {
        path: Path.next(Path.parent(editor.selection.focus.path)),
        offset: 0
      }
    });

    if (wrapped === true) {
      Transforms.insertText(editor, ' ');
    }

  },
  getLinkNodes(editor) {

    return Editor.nodes(editor, {
      match: isLinkNode
    });

  },
  insertWord(editor, word) {
    Transforms.insertText(editor, `${word} `);
  },
  insertSpinner(editor, insertOptions) {

    const { selection } = editor;

    const isCollapsed = selection && Range.isCollapsed(selection);

    const spinner = {
      type: 'spinner',
      children: [{ text: '' }]
    };
    const textNode = { text: ' ' };

    if (isCollapsed === true) {
      Transforms.insertNodes(editor, [spinner, textNode], insertOptions);
    } else {
      Transforms.wrapNodes(editor, [spinner, textNode], { split: true });
    }

  },
  removeSpinner(editor, options) {

    Transforms.removeNodes(editor, options);

  },
  insertImage(editor, file, options) {

    const image = {
      type: 'image',
      url: file.url,
      children: [{ text: '' }],
      resizable: true
    };
    Transforms.insertNodes(editor, [image], options);

  },
  deleteBackward(editor, charactersCount) {

    for (let index = 0; index < charactersCount; index++) {
      editor.deleteBackward('character');
    }

  },
  isPointAtStartOfTheLine(editor, point) {
    return point.offset === 0;
  },
  isAtStartOfTheLine(editor) {

    const { selection } = editor;

    if (!selection) {
      return false;
    }

    return selection.anchor.offset === 0;

  },
  swtichToColumnsLayout(editor, targetPath) {

    multipleColumnsUtilities.toggle(editor, targetPath);

  },
  insertColumnsLayout(editor, nodePath) {

    multipleColumnsUtilities.insert(editor, nodePath);

  },
  removeFormatting(editor) {

    const selection = editor.selection;

    const collapsed = selection !== null && Range.isCollapsed(selection);

    if (collapsed === true) {
      return;
    }

    for (let mark of marks) {

      HelptapEditor.removeMark(editor, mark);

    }

    Transforms.setNodes(editor, { alignment: void 0 });

    toggleBlock(editor, types.DEFAULT);

  },
  setTextAttribute(editor, attribute, value) {

    const selection = editor.selection;

    const collapsed = selection !== null && Range.isCollapsed(selection);

    if (collapsed === true) {
      return;
    }

    const attributeExists = HelptapEditor.hasTextAttribute(editor, attribute, value);

    if (attributeExists === true) {
      HelptapEditor.removeMark(editor, attribute);
    } else {
      HelptapEditor.addMark(editor, attribute, value);
    }

    // ReactEditor.focus(editor);

  },
  unsetTextAttribute(editor, attribute) {

    const selection = editor.selection;

    const collapsed = selection !== null && Range.isCollapsed(selection);

    if (collapsed === true) {
      return;
    }

    HelptapEditor.removeMark(editor, attribute);

  },
  textAttributeExists(editor, attribute) {

    const marks = Editor.marks(editor);

    if (marks === null) {
      return false;
    }

    return typeof marks[attribute] !== 'undefined';

  },
  hasTextAttribute(editor, attribute, value) {

    const marks = Editor.marks(editor);

    if (marks === null) {
      return false;
    }

    return marks[attribute] === value;

  },
  getTextAttribute(editor, attribute) {

    const marks = Editor.marks(editor);

    if (marks === null) {
      return false;
    }

    const value = marks[attribute];

    if (typeof value === 'undefined') {
      return null;
    }

    return value;

  },
  setAttributes(editor, attributes, path) {

    Transforms.setNodes(editor, attributes, {
      at: path
    });

  },
  hasAttribute(editor, attribute) {

    const [node] = Editor.nodes(editor, {
      match: hasAttribute(attribute)
    });

    return !!node;
  },
  getNodesByAttribute(editor, attribute) {

    return Editor.nodes(editor, {
      match: hasAttribute(attribute)
    });

  },
  collapse(editor) {
    Transforms.collapse(editor, { edge: 'end' });
  },
  moveCursorToStartOfTheLine(editor, at) {

    Transforms.select(editor, {
      path: [at[0], 0],
      offset: 0
    });

  },
  focusNextNode(editor, path) {

    const nextNodePath = Path.next(path);
    const nextNode = Node.get(editor, nextNodePath);

    Transforms.select(editor, {
      path: nextNodePath,
      offset: Node.string(nextNode).length
    });

    ReactEditor.focus(editor);

  },
  deleteNodeByPathV2(editor, path) {

    Transforms.select(editor, path);
    Transforms.delete(editor);

  },
  getPreviousNode(editor, anchor = [0]) {

    if (editor.selection !== null) {
      anchor = editor.selection.anchor;
    }

    const [, previousNodeLocation] = Editor.first(editor, anchor);
    const [previousNode] = Editor.above(editor, { at: previousNodeLocation });

    return previousNode;

  },
  getSingleTextNode(node) {
    const texts = Node.texts(node);
    const textNodeEntries = Array.from(texts);

    if (textNodeEntries.length > 1) {
      return null;
    }

    if (textNodeEntries.length === 0) {
      return null;
    }

    return textNodeEntries[0];

  },
  isEmptyNode(node) {

    const textNodeEntry = HelptapEditor.getSingleTextNode(node);

    if (textNodeEntry === null) {
      return false;
    }

    return textNodeEntry[0].text === '';

  },
  isNodeEmptyByPath(editor, path) {

    const string = Editor.string(editor, path);

    return string.trim() === '';

  },
  handleDeleteBackward(editor, topLevelNode, topLevelNodePath) {

    if (topLevelNode.type === types.BULLETED_LIST) {
      return listUtilities.handleDeleteBackward(editor, topLevelNode, topLevelNodePath);
    } else if (topLevelNode.type === types.NUMBERED_LIST) {
      return listUtilities.handleDeleteBackward(editor, topLevelNode, topLevelNodePath);
    } else if (topLevelNode.type === types.TOGGLE_LIST_HEAD) {
      return toggleListItemUtilities.handleDeleteBackward(editor, topLevelNode, topLevelNodePath);
    } else if (topLevelNode.type === types.DEFAULT) {
      return paragraphUtilities.handleDeleteBackward(editor, topLevelNode, topLevelNodePath);
    } else if ([types.HEADING_ONE, types.HEADING_TWO, types.HEADING_THREE, types.HEADING_FOUR, types.HEADING_FIVE, types.HEADING_SIX].includes(topLevelNode.type)) {
      return headingUtilities.handleDeleteBackward(editor, topLevelNode, topLevelNodePath);
    }

    if (editor.selection.focus.offset === 0) {
      toggleBlock(editor, types.DEFAULT, {
        ...paragraphUtilities.buildNode(),
        backgroundColor: void 0,
        textColor: void 0
      });
      return true;
    }

    return false;

  },
  tryCompleteList(editor, listItemTextLocation, removableChildrenCount) {

    const listLocation = listItemTextLocation.slice(0, -2);
    const listNode = Node.get(editor, listLocation);
    const listItems = listNode.children;

    if (listItems.length > (listItemTextLocation[1] + 1)) {
      const listItemLocation = Path.parent(listItemTextLocation);
      Transforms.removeNodes(editor, {
        at: listItemLocation
      });
      Transforms.splitNodes(editor, { at: listItemLocation });
      const paragraphLocation = Path.next(Path.parent(listItemLocation));
      Transforms.insertNodes(editor, paragraphUtilities.buildNode(), { at: paragraphLocation });
      Transforms.select(editor, paragraphLocation);
      return true;
    }
    const listItemsCount = listItems.length;
    const listItemLocation = Path.parent(listItemTextLocation);

    if (listItemsCount > 1) {
      Transforms.removeNodes(editor, {
        at: listItemLocation
      });
      const paragraphLocation = Path.next(listLocation);
      HelptapEditor.insertBlock(editor, types.DEFAULT, {}, paragraphLocation);
      setTimeout(function someTimeLater() {
        Transforms.select(editor, paragraphLocation);
      }, 1);
    } else if (listItemsCount === 1) {
      toggleBlock(editor, types.DEFAULT, {
        ...defaultStyles,
        children: [{ text: '' }]
      });
    }

    return true;

  },
  getClosestParentNode(editor, path) {

    let node = Node.get(editor, path);

    while (Text.isText(node)) {

      path = Path.parent(path);
      node = Node.get(editor, path);

    }

    return node;

  },
  processEnterKey(editor) {

    if (handleHeaderEnter(editor)) {
      return;
    }

    let anchor = [0];

    if (editor.selection !== null) {
      anchor = editor.selection.anchor;
    }

    const [, previousTextNodeLocation] = Editor.first(editor, anchor);
    const [previousNode, previousNodeLocation] = Editor.parent(editor, previousTextNodeLocation);
    const actions = [() => {
      if (multipleColumnsUtilities.isColumnParent(editor) === true) {
        return multipleColumnsUtilities.tryMoveToNextTopLevelBlock(editor, previousNode, previousNodeLocation);
      }
    }, () => {
      if (dataCollectionUtilities.isDataCollectionNodeDescendant(editor, editor.selection) === true) {
        const [, nodePath] = dataCollectionUtilities.getParentDataCollectionNode(editor, editor.selection);
        const nextNodePath = Path.next(nodePath);
        paragraphUtilities.insert(editor, nextNodePath);
        Transforms.select(editor, nextNodePath);
        return true;
      }
    }, () => {
      if (toggleListItemUtilities.isHeadDescendantSelected(editor) === true) {
        return toggleListItemUtilities.handleEnterKeyOnHead(editor, previousNode, previousNodeLocation);
      }
    }, () => {
      if (dataCollectionUtilities.isDatabaseConnectedNodeSelected(editor) === true) {
        Transforms.collapse(editor, { edge: 'end' });
        Transforms.insertNodes(editor, paragraphUtilities.buildNode());
        return true;
      }
    }, () => {
      return layoutUtilities.handleEnterKey(editor);
    }];

    for (let action of actions) {

      const result = action();

      if (result === true) {
        return true;
      }

    }

    return false;

  },
  insertNextBlock(editor, event) {

    const [voidNode] = Editor.nodes(editor, {
      match(node) { return serializer.voidElements.includes(node.type) || [types.GALLERY, types.CAROUSEL].includes(node.type); },
      mode: 'lowest'
    });

    if (!voidNode) {
      return;
    }

    const node = voidNode[0];
    const voidNodePath = voidNode[1];

    let insertPath = Path.next(voidNodePath);

    if ([types.GALLERY_ITEM, types.CAROUSEL_ITEM].includes(node.type)) {
      insertPath = Path.next(Path.parent(voidNodePath));
    }

    HelptapEditor.paragraph.insert(editor, insertPath);
    event.preventDefault();

  },
  isCollapsed(editor) {

    const { selection } = editor;

    return selection !== null && Range.isCollapsed(selection);

  },
  isMultilineSelection(editor) {

    // Multi line selection is considered as multi line selection,
    // if there are more than 2 number lines
    // if there is only 2 lines, the second line offset must be 0

    const selection = editor.selection;

    if (selection === null) {
      return false;
    }

    let start = selection.anchor;
    let end = selection.focus;

    if (start.path[0] > end.path[0]) {
      end = selection.anchor;
      start = selection.focus;
    } else if (start.path[0] === end.path[0] && start.offset > end.offset) {
      end = selection.anchor;
      start = selection.focus;
    }

    const isSelectionInOneLine = start.path[0] === end.path[0];

    if (isSelectionInOneLine === true) {
      return false;
    }

    const nextLineIndex = start.path[0] + 1;
    // if the selection starts in one line & ends in next immediate line
    // & if the ending line has no selection
    // current selection is not multi line selection.
    if (nextLineIndex === end.path[0] && end.offset === 0) {
      return false;
    }

    return true;

  },
  isOnlyTextNodesSelected(editor) {

    const { selection } = editor;
    let allTextChildren = true;
    // Iterate over all nodes in the selection
    const filterOptions = {
      at: selection,
      match: (node) => SlateElement.isElement(node),
      mode: 'highest'
    };
    for (const [node] of Editor.nodes(editor, filterOptions)) {
      if (Editor.isVoid(editor, node) === true) {
        allTextChildren = false;
        break;
      }
      for (const child of node.children) {
        if (Text.isText(child) === false) {
          if (textContainers.indexOf(child.type) !== -1) {
            continue;
          }
          allTextChildren = false;
          break;
        }
      }

      if (!allTextChildren) {
        break;
      }
    }

    return allTextChildren;

  },
  isPathFocused(editor, path) {
    const { selection } = editor;
    if (selection && Range.isRange(selection)) {
      return Range.includes(selection, path);
    }
    return false;
  },
  handleSpaceKey(editor, event, disabledElements) {

    const wordStat = HelptapEditor.getPreviousWord(editor);

    const statsArray = textUtilities.parseText(wordStat.word);

    if (statsArray.length === 0) {
      return;
    }

    const stat = statsArray[0];
    const statType = stat.type;

    if (statType === 'link') {
      HelptapEditor.wrapLinkAt(editor, stat.url, stat.text, {
        anchor: {
          path: wordStat.leafFocus.path,
          offset: wordStat.start,
        },
        focus: {
          path: wordStat.leafFocus.path,
          offset: wordStat.end,
        }
      });
    } else if (statType === 'text') {
      nodeConversationUtilities.tryConvertCurrentNode(editor, event, wordStat, disabledElements);
    }

  },
  handleSpaceKeyOld(editor) {

    let anchor = [0];

    if (editor.selection !== null) {
      anchor = editor.selection.anchor;
    }

    const [, previousNodeLocation] = Editor.first(editor, anchor);
    const [previousNode] = Editor.above(editor, { at: previousNodeLocation });
    const textNodeEntry = HelptapEditor.getSingleTextNode(previousNode);

    if (textNodeEntry === null) {
      return;
    }

    if (blockLevelElements.indexOf(previousNode.type) === -1) {
      return;
    }

    const nodeText = textNodeEntry[0].text;

    if (nodeText === '*') {
      toggleBlock(editor, types.BULLETED_LIST, {
        ...defaultStyles,
        children: [{ text: '' }]
      });
    } else if (nodeText === '1.') {
      toggleBlock(editor, types.NUMBERED_LIST, {
        ...defaultStyles,
        children: [{ text: '' }]
      });
    } else if (nodeText === '[]') {
      toggleBlock(editor, types.CHECK_LIST_ITEM, {
        ...defaultStyles,
        children: [{ text: '' }]
      });
    }

  },
  getTextBetween(editor, from, to) {

    const selection = editor.selection;

    if (Range.isCollapsed(selection) === false) {
      return;
    }

    return Array.from(Node.texts(editor, { from, to }));

  },
  deleteNodeByPath(editor, path) {

    const options = {
      at: path
    };

    Transforms.removeNodes(editor, options);

  },
  tryAddMdash(editor) {

    const [node] = Editor.first(editor, editor.selection);

    const selection = editor.selection;
    const from = pathUtilities.cloneFocus(selection.focus);

    if (from.offset === 0) {
      return;
    }

    from.offset--;

    const string = Node.string(node);
    const previousCharacter = string.charAt(from.offset);

    if (previousCharacter !== '-') {
      return;
    }

    Transforms.delete(editor, { at: from, unit: 'character' });
    Transforms.insertText(editor, '—', { at: from });

    return true;

  },
  tryInsertNewLine(editor) {

    const children = editor.children;

    const [node, nodePath] = Editor.first(editor, editor.selection);

    if (nodePath[0] < (children.length - 1)) {
      const [nextNode] = Editor.next(editor, { at: Path.parent(nodePath) });
      if (HelptapEditor.isVoid(editor, nextNode) === false) {
        return;
      }
    }

    HelptapEditor.insertParagraph(editor);

  },
  getPreviousCharacter(editor) {

    const focus = editor.selection.focus;
    const [leaf] = Editor.leaf(editor, focus);
    const previousCharacter = leaf.text.substring(focus.offset - 1, focus.offset);

    return previousCharacter;

  },
  getPreviousWord(editor) {

    const focus = editor.selection.focus;
    const [leaf] = Editor.leaf(editor, focus);
    const text = leaf.text.substring(0, focus.offset);
    const textLength = text.length;

    for (let index = textLength - 1; index >= 0; index--) {

      const character = text.charAt(index);

      if (character === WHITE_SPACE_CHARACTER) {
        const start = index + 1;
        const word = text.substring(start, focus.offset);
        const stats = {
          word,
          leafFocus: focus,
          start,
          end: focus.offset
        };
        return stats;
      }

    }

    const word = text.substring(0, focus.offset);

    const stats = {
      word,
      leafFocus: focus,
      start: 0,
      end: focus.offset
    };
    return stats;

  },
  addSingleProducerMeeting(editor, postId, insertPoint = []) {

    const blockOptions = {
      at: insertPoint,
      attributes: {
        postId
      },
      padding: [0, 0, 0, 0],
      margin: [0, 0, 0, 0]
    };

    HelptapEditor.insertBlock(editor, types.SINGLE_PRODUCER_MEETING, blockOptions);

    HelptapEditor.insertBlock(editor, types.DEFAULT);

  },
  insertHTFile(editor, fileType, insertPoint = [], rawFile = null) {

    const attributes = {
      file: null,
      fileType,
      showFileDialog: true,
      rawFile,
      altText: ''
    };

    const nodeOptions = {
      at: insertPoint,
      attributes,
      padding: [5, 5, 5, 5],
      resizable: true,
      margin: [0, 'auto', 0, 'auto'],
      marginLeft: 'auto',
      marginRight: 'auto'
    };

    const startOfTheLine = HelptapEditor.isPointAtStartOfTheLine(editor, insertPoint);

    if (startOfTheLine === true) {
      return toggleBlock(editor, types.HT_FILE, nodeOptions);
    }

    HelptapEditor.insertBlock(editor, types.HT_FILE, nodeOptions);

  },
  setNodeError(editor, elementPath, error) {
    HelptapEditor.setAttributes(editor, {
      error
    }, elementPath);
  },
  setNodeBusy(editor, elementPath) {
    HelptapEditor.setAttributes(editor, {
      busy: true
    }, elementPath);
  },
  setNodeIdle(editor, elementPath) {
    HelptapEditor.setAttributes(editor, {
      busy: false
    }, elementPath);
  },
  isBusy(editor) {

    if (editor === null) {
      return false;
    }

    for (let [child] of Node.children(editor, [])) {

      if (child.busy === true) {
        return true;
      }

    }

    return false;

  },
  setContent(editor, content) {

    Transforms.delete(editor, {
      at: {
        anchor: Editor.start(editor, []),
        focus: Editor.end(editor, []),
      },
    });

    Transforms.setNodes(editor, content);

  },
  focus(editor) {
    ReactEditor.focus(editor);
  },
  scrollToFocusedNode(editor) {
    // Assuming 'editor' is your Slate editor instance
    const { selection } = editor;
    if (!selection) return;

    const domRange = ReactEditor.toDOMRange(editor, selection);
    let domNode = domRange.startContainer;

    while (domNode.nodeType !== 1) { // Node.ELEMENT_NODE
      domNode = domNode.parentNode;
    }

    // Scroll the domNode into view
    domNode.scrollIntoView({ behavior: 'smooth', block: 'center' });
  },
  focusAt(editor, path) {
    Transforms.select(editor, {
      anchor: {
        path,
        offset: 0
      },
      focus: {
        path,
        offset: 0
      }
    });

    HelptapEditor.focus(editor);

  },
  insertHTStartupEventTemplate(editor, options) {

    const attributes = {
      postId: options.postId
    };

    const nodeOptions = {
      at: options.insertPoint,
      attributes,
      width: '100%'
    };

    if (options.insertPoint === void 0) {

      HelptapEditor.insertBlock(editor, types.HT_STARTUP_EVENT_TEMPLATE, nodeOptions);

      return;
    }

    const startOfTheLine = HelptapEditor.isPointAtStartOfTheLine(editor, options.insertPoint);
    HelptapEditor.insertParagraph(editor, {});
    if (startOfTheLine === true) {
      return toggleBlock(editor, types.HT_STARTUP_EVENT_TEMPLATE, nodeOptions);
    }

    HelptapEditor.insertBlock(editor, types.HT_STARTUP_EVENT_TEMPLATE, nodeOptions);

  },
  removedEmbeddedNode(editor, path, element) {

    const response = {
      removeColumn: false
    };

    if (Path.equals(path, [0]) === true) {
      response.removeColumn = true;
    } else {
      HelptapEditor.deleteNodeByPath(editor, path);
    }

    return response;

  },
  tryAndSelectOneOfTheSibling(editor, startPath) {

    let nodeEntry = Editor.next(editor, { at: startPath });
    let currentPath = startPath;

    let selected = false;

    while (nodeEntry !== void 0) {

      currentPath = nodeEntry[1];

      if (Editor.isVoid(editor, nodeEntry[0]) === true) {
        nodeEntry = Editor.next(editor, { at: currentPath });
      } else {
        selected = true;
        break;
      }

    }

    if (selected === false) {
      currentPath = Path.next(currentPath);
      HelptapEditor.insertParagraph(editor, {}, currentPath);
    }

    Transforms.select(editor, {
      path: currentPath,
      offset: 0
    });

    ReactEditor.focus(editor);

    return false;

  },
  tryFocusFirstNode(editor) {
    HelptapEditor.tryAndSelectOneOfTheSibling(editor, [0, 0]);
  },
  tryFocus(editor) {

    if (editor.selection === null) {
      HelptapEditor.tryFocusFirstNode(editor);
      return;
    }

    const [node, nodePath] = Editor.above(editor);

    if (Editor.isVoid(editor, node) === true) {
      HelptapEditor.tryAndSelectOneOfTheSibling(editor, nodePath);
    } else {
      ReactEditor.focus(editor);
    }

  },
  tryFocusV2(editor) {

    ReactEditor.focus(editor);

  },
  addColumnsV2(editor) {

    const columnsV2 = [{
      ...defaultStyles,
      type: 'columns-v2',
      children: [{
        type: 'column-node',
        children: [HelptapEditor.createDefaultBlock()]
      }, {
        type: 'column-node',
        children: [HelptapEditor.createDefaultBlock()]
      }]
    }];

    Transforms.insertNodes(editor, columnsV2);

  },
  focusOnFirstLine(editor) {

    Transforms.select(editor, {
      anchor: Editor.start(editor, []),
      focus: Editor.start(editor, [])
    });

    ReactEditor.focus(editor);

  },
  isEmpty(editor) {

    // Define emptiness!!
    // Emptiness is the state of mind where nothing happens, 
    //      but still being aware of what is happening.
    // Define Slate Editor emptiness
    // Slate is considered to be empty 
    //    when it has zero void nodes & 
    //    string content of all the non void nodes are empty.

    if (editor.children.length > 1) {
      return false;
    }

    if (Editor.isVoid(editor, editor.children[0]) === true) {
      return false;
    }

    return Node.string(editor).length === 0;

  },
  isContentEmpty(content) {

    return Node.string(content).length === 0;

  },
  insertNodes(editor, nodes, options = {}) {

    Transforms.insertNodes(editor, nodes, options);

  },
  insertNodesReplacingEmptyBlock(editor, nodes) {

    const parentBlockPath = [editor.selection.anchor.path[0]];
    const parentBlock = Node.get(editor, parentBlockPath);
    const isParentVoid = Editor.isVoid(editor, parentBlock);
    const isParentEmpty = Node.string(parentBlock) === '';

    if (parentBlock.type === types.LAYOUT) {
      layoutUtilities.insertReplacingEmptyBlock(editor, nodes);
      return;
    }

    if (isParentEmpty === false || isParentVoid === true) {
      HelptapEditor.insertNodes(editor, nodes);
      return;
    }

    Transforms.insertNodes(editor, nodes, { at: parentBlockPath });
    const emptyBlockPath = [parentBlockPath[0] + nodes.length];
    Transforms.removeNodes(editor, { at: emptyBlockPath });

  },
  clearSelection(editor) {
    Transforms.deselect(editor);
  },
  moveNodes(editor, options) {
    Transforms.moveNodes(editor, options);
    Transforms.deselect(editor);
  },
  replaceChildren(editor, path, children, currentChildrenLength) {

    Editor.withoutNormalizing(editor, function replaceChildren() {

      const childrenLength = children.length;

      for (let index = 0; index < childrenLength; index++) {
        Transforms.insertNodes(editor, children[index], { at: [...path, currentChildrenLength + index] });
      }

      for (let index = 0; index < currentChildrenLength; index++) {
        Transforms.removeNodes(editor, { at: [...path, 0] });
      }

    });

  },
  replaceContent(editor, content) {

    Editor.withoutNormalizing(editor, function replaceContent() {

      const currentChildrenLength = editor.children.length;
      const childrenLength = content.length;

      for (let index = 0; index < childrenLength; index++) {
        Transforms.insertNodes(editor, content[index], { at: [currentChildrenLength + index] });
      }

      for (let index = 0; index < currentChildrenLength; index++) {
        Transforms.removeNodes(editor, { at: [0] });
      }

    });

  },
  setChildren(editor, path, children) {

    const node = Editor.node(editor, path);
    const numberOfChildren = node[0].children.length;
    for (let i = numberOfChildren - 1; i >= 0; i--) {
      const childPath = path.concat(i);
      Transforms.removeNodes(editor, { at: childPath });
    }

    // Insert new text children
    children.forEach((child, index) => {
      const childPath = path.concat(index);
      Transforms.insertNodes(editor, child, { at: childPath });
    });

  },
  replaceNode(editor, path, newNode) {

    Editor.withoutNormalizing(editor, function replaceChildren() {

      const options = {
        at: path
      };

      Transforms.removeNodes(editor, options);
      Transforms.insertNodes(editor, newNode, options);

    });

  },
  insertAfter(editor, path, newNode) {
    console.log('Not implemented');
  },
  cloneChildren(children) {
    return children.map(function cloneNode(node) {

      if (SlateElement.isElement(node)) {
        return {
          ...cloneProperties(node),
          children: HelptapEditor.cloneChildren(node.children),
        };
      }

      return { ...node };

    });
  },
  cloneNode(node) {
    return HelptapEditor.cloneChildren([node])[0];
  },
  getClosestIndentableNodePath(editor) {

    let currentPath = editor.selection.anchor.path;

    while (currentPath.length > 0) {
      const currentNode = Node.get(editor, currentPath);
      if (indentationChildren.includes(currentNode.type)) {
        return currentPath;
      }
      currentPath = Path.parent(currentPath);
    }

    return null;

  },
  getDraggableNodePath(editor) {

    let currentPath = editor.selection.anchor.path;

    while (currentPath.length > 0) {
      const currentNode = Node.get(editor, currentPath);
      if (serializer.draggableElements.includes(currentNode.type)) {
        return currentPath;
      }
      currentPath = Path.parent(currentPath);
    }

    return null;

  },
  handleShiftTabKeyEvent(event, editor) {

    if (listUtilities.outdent(editor) === true) {
      return true;
    }

    const indentableNodePath = HelptapEditor.getClosestIndentableNodePath(editor);

    if (indentableNodePath === null) {
      return false;
    }

    return indentationUtilities.unindent(editor, indentableNodePath);

  },
  handleTabKeyEvent(event, editor) {

    if (!editor.selection) {
      return;
    }

    if (listUtilities.nest(editor) === true) {
      return true;
    }

    const ancestors = Path.ancestors(editor.selection.anchor.path);
    const topLevelNodePath = ancestors[1];
    const [topLevelNode] = Editor.node(editor, topLevelNodePath);
    const editableNodePath = HelptapEditor.getClosestEditableNodePath(editor);

    if (editableNodePath === null) {
      if (topLevelNode.type === types.TABLE) {
        return tableUtilities.focusNextCell(editor, topLevelNode, ancestors);
      }
      return;
    }

    const editableNode = Node.get(editor, editableNodePath);
    const indentableNodePath = HelptapEditor.getClosestIndentableNodePath(editor);

    if (editableNode.type === types.TOGGLE_LIST_HEAD) {
      return toggleListItemUtilities.handleTabKeyEvent(editor, editableNode, editableNodePath);
    } else if (indentableNodePath !== null) {
      return indentationUtilities.indent(editor, indentableNodePath);
    }

  },
  insertNodeFromClipboard(editor, node) {

    if (synchronizedPageRegionUtilities.isSynchronizedPageRegion(node) === true) {
      return synchronizedPageRegionUtilities.insert(editor, node);
    }

    return HelptapEditor.insertNodes(editor, node);

  },
  getParentBlock(editor) {

    const [parentBlock] = Editor.node(editor, [editor.selection.anchor.path[0]]);

    return parentBlock;

  },
  getSelectedText(editor) {

    return Editor.string(editor, editor.selection);

  },
  isNextNodeExists(editor, path) {

    try {

      const result = Editor.next(editor, { at: path });

      return result !== void 0;

    } catch (exception) {
      return false;

    }

  },
  focusNodeByPath(editor, path) {
    // Move the selection to the specified path
    Transforms.select(editor, path);

    // Focus the editor
    ReactEditor.focus(editor);
  },
  isSingleBlockSelected(editor) {

    const { selection } = editor;

    if (selection && !Range.isCollapsed(selection)) {
      return Path.equals(selection.anchor.path.slice(0, -1), selection.focus.path.slice(0, -1));
    }

    return true;

  },
  isTextSelected(editor) {

    const singleBlockSelected = HelptapEditor.isSingleBlockSelected(editor);

    if (singleBlockSelected === false) {
      return false;
    }

    return true;

  },
  getNodeByPath(editor, path) {

    return Node.get(editor, path);

  },
  isLastNodeEditable(editor) {

    const lastNodePath = [editor.children.length - 1];
    const lastNode = Node.get(editor, lastNodePath);

    return textElements.indexOf(lastNode.type) !== -1;

  },
  tryInsertParagraphInTheEnd(editor) {

    if (HelptapEditor.isLastNodeEditable(editor) !== true) {
      HelptapEditor.insertParagraph(editor, {}, [editor.children.length]);
    }

    Transforms.select(editor, [editor.children.length - 1]);
    Transforms.collapse(editor, { edge: 'end' });

  },
  isNodeActive(editor, nodePath) {

    const selection = editor.selection;

    if (!selection) {
      return false;
    }

    const edgePath = Path.parent(selection.focus.path);

    return Path.equals(edgePath, nodePath);

  },
  isEmptyV2(editor, node) {
    const text = Node.string(node);
    return text === '';
  },
  getClosestNodePathFromPathByType(editor, path, type) {

    let currentPath = path;

    while (currentPath.length > 0) {
      const currentNode = Node.get(editor, currentPath);
      if (currentNode.type === type) {
        return currentPath;
      }
      currentPath = Path.parent(currentPath);
    }

    return null;

  },
  getClosestNodeFromPathByType(editor, path, type) {

    const nodePath = HelptapEditor.getClosestNodePathFromPathByType(editor, path, type);

    if (nodePath === null) {
      return null;
    }

    return Node.get(editor, nodePath);

  },
  getClosestEditableNodePathFromPath(editor, path) {

    let currentPath = path;

    while (currentPath.length > 0) {
      const currentNode = Node.get(editor, currentPath);
      if (serializer.editableElements.includes(currentNode.type)) {
        return currentPath;
      }
      currentPath = Path.parent(currentPath);
    }

    return null;

  },
  getClosestEditableNodePath(editor) {

    let currentPath = editor.selection.anchor.path;

    return HelptapEditor.getClosestEditableNodePathFromPath(editor, currentPath);

  },
  getClosestEditableNode(editor) {

    const path = HelptapEditor.getClosestEditableNodePath(editor);

    if (path === null) {
      return null;
    }

    return Node.get(editor, path);

  },
  select(editor, path) {

    const node = Node.get(editor, path);
    const nodeType = node.type;

    if (nodeType === types.DEFAULT) {
      paragraphUtilities.select(editor, path, node);
    } else if (nodeType === types.TOGGLE_LIST_ITEM) {
      toggleListItemUtilities.select(editor, path, node);
    } else {
      Transforms.select(editor, path);
    }

  },
  determineTargetNodePath(editor, path) {

    let currentPath = path;

    while (currentPath.length > 0) {
      const currentNode = Node.get(editor, currentPath);
      if (serializer.editableElements.includes(currentNode.type)) {
        return currentPath;
      }
      currentPath = Path.parent(currentPath);
    }

    return null;
  }

  ,
  getTargetBlockNodePath(editor, path) {

    let targetNodePath = HelptapEditor.determineTargetNodePath(editor, path);

    if (targetNodePath === null) {
      return null;
    }

    while (targetNodePath.length > 0) {
      const currentNode = Node.get(editor, targetNodePath);
      if (blockLevelElements.includes(currentNode.type) === true) {
        return targetNodePath;
      }
      targetNodePath = Path.parent(targetNodePath);
    }

    return null;

  },
  trySelectPreviousNode(editor) {

    const selection = editor.selection;

    if (selection === null) {
      return;
    }
    const currentPath = selection.anchor.path;

    if (selection.focus.offset !== 0) {
      return;
    }

    const toggleNodePath = toggleListItemUtilities.getClosestToggleNodePath(editor, currentPath);

    if (toggleNodePath === null) {
      return;
    }

    const blockNodePath = HelptapEditor.getTargetBlockNodePath(editor, currentPath);

    if (blockNodePath === null) {
      return;
    }

    if (toggleNodePath.length > blockNodePath.length) {
      return;
    }

    // toggleListItemUtilities.expandParents(editor, toggleNodePath);
    // toggleListItemUtilities.trySelectHead(editor, blockNodePath);

  },
  isEditableNodeSelected(editor) {
    const path = HelptapEditor.getClosestEditableNodePath(editor);

    return path !== null;
  },
  canRenderNodeTypeButtons(editor) {

    const node = HelptapEditor.getClosestEditableNode(editor);

    if (node === null) {
      return false;
    }

    if (node.type === types.BUTTON || node.type === types.AUTHENTICATOR_BUTTON) {
      return false;
    }

    return true;

  },
  canRenderQuoteButtonSection(editor) {
    return HelptapEditor.canRenderNodeTypeButtons(editor);
  },
  canRenderAlignmentButtonsSection(editor) {
    const node = HelptapEditor.getClosestEditableNode(editor);

    if (node === null) {
      return false;
    }

    return true;
  },
  canRenderLinkButtonsSection(editor) {
    return HelptapEditor.canRenderNodeTypeButtons(editor);
  },
  canRenderHeadingButtonsSection(editor) {
    return HelptapEditor.canRenderNodeTypeButtons(editor);
  },
  isNodeSelected(editor, node) {

    const { selection } = editor;
    if (!selection) {
      return false;
    }

    const [match] = Editor.nodes(editor, {
      at: selection,
      match: n => n === node,
    });

    return !!match;

  },
  isNodeDirectlySelected(editor, node, nodePath) {

    const { selection } = editor;
    if (!selection) {
      return false;
    }

    const [match] = Editor.nodes(editor, {
      at: selection,
      match: function(n) { return n === node; }
    });

    if (!match) {
      return false;
    }

    const [childMatch] = Editor.nodes(editor, {
      at: selection,
      match: function(n, p) {
        return SlateElement.isElement(n) && Path.isDescendant(p, nodePath);
      }
    });

    return !childMatch;

  },
  getDirectlySelectedBlockKeys(editor) {

    const selection = editor.selection;

    if (!selection) {
      return [];
    }

    const elementPaths = [];

    for (const entry of Editor.nodes(editor, { at: selection, match: function matchElement(node) { return SlateElement.isElement(node); } })) {
      elementPaths.push(entry[1]);
    }

    const keys = [];

    for (const path of elementPaths) {

      let hasSelectedDescendant = false;

      for (const otherPath of elementPaths) {
        if (Path.isDescendant(otherPath, path) === true) {
          hasSelectedDescendant = true;
          break;
        }
      }

      if (hasSelectedDescendant === false) {
        keys.push(path.join('.'));
      }

    }

    if (keys.length > 1) {
      return [];
    }

    return keys;

  },
  getNodePathsFromSelection(editor, selection, mode = 'all') {

    const nodePaths = [];
    for (const [node, path] of Editor.nodes(editor, { at: selection, mode })) {
      if (SlateElement.isElement(node)) {
        nodePaths.push(path);
      }
    }

    return nodePaths;

  },
  removeTextNodeFromPath(editor, path) {
    let finalPath = [...path];

    let node = Node.get(editor, finalPath);

    while (Text.isText(node) === true || serializer.draggableElements.indexOf(node.type) === -1) {
      finalPath = Path.parent(finalPath);
      node = Node.get(editor, finalPath);
    }

    return finalPath;

  },
  ensureSamePathLevels(path1, path2) {

    if (path1.length > path2.length) {
      path1.splice(path2.length);
    } else {
      path2.splice(path1.length);
    }

  },
  countChildrenOfType(editor, node, childType) {

    const children = node.children;
    let count = 0;

    for (let child of children) {

      if (child.type !== childType) {
        continue;
      }

      count++;

    }

    return count;

  },
  getSelectedContent(editor) {
    const { selection } = editor;
    if (!selection || Range.isCollapsed(selection)) return null;
    const content = Editor.fragment(editor, selection);

    if (Array.isArray(content)) {
      return content;
    }

    if (blockLevelElements.includes(content.type) === false) {
      return [{ ...paragraphUtilities.buildNode(), children: [content] }];
    }

    return [content];

  },
  trim(editor, nodes) {

    var leftTrimmed = [];
    var trimming = true;

    for (let node of nodes) {

      if (HelptapEditor.isEmptyV2(editor, node) === false || trimming === false) {
        leftTrimmed.push(node);
        trimming = false;
      }

    }
    const trimmed = [];
    trimming = true;
    for (let node of leftTrimmed.reverse()) {

      if (HelptapEditor.isEmptyV2(editor, node) === false || trimming === false) {
        trimmed.push(node);
        trimming = false;
      }

    }

    return trimmed.reverse();

  }

};

export default HelptapEditor;
